import {
  Box,
  Button,
  FormLabel,
  Grid,
  IconButton,
  ToggleButton,
  Typography,
} from '@mui/material';
import ToggleButtonGroup,
{
  toggleButtonGroupClasses,
} from '@mui/material/ToggleButtonGroup';
import { styled, createTheme } from '@mui/material/styles';
import _ from 'lodash';

export const BODY_FONT_FAMILY = ['"Exo 2"', 'arial', 'sans-serif'].join(',');
export const HEADER_FONT_FAMILY = ['"Space Grotesk"', 'sans-serif'].join(',');
// see variables.less
export const MONOSPACE_FONT_FAMILY = 'Menlo, Monaco, Consolas, "Courier New", monospace';

export const commonTheme = {
  breakpoints: {
    values: {
      xs: 0, // [0, 450]
      sm: 451, // [451, 770]
      md: 771, // [771, 960]
      lg: 961, // [961, 1220]
      xl: 1221, // [1220, ]
    },
  },
  palette: {
    primary: {
      main: '#1E93A5',
      hover: '#26B8CE',
    },
    secondary: {
      main: '#009AB4',
    },
  },
  typography: {
    fontFamily: BODY_FONT_FAMILY,
    body1: {
      fontFamily: BODY_FONT_FAMILY,
    },
    body2: {
      fontFamily: BODY_FONT_FAMILY,
    },
    overline: {
      fontFamily: HEADER_FONT_FAMILY,
    },
    caption: {
      fontFamily: HEADER_FONT_FAMILY,
    },
    subtitle1: {
      fontFamily: HEADER_FONT_FAMILY,
    },
    subtitle2: {
      fontFamily: HEADER_FONT_FAMILY,
    },
    h1: {
      fontFamily: HEADER_FONT_FAMILY,
      lineHeight: 1.25,
    },
    h2: {
      fontFamily: HEADER_FONT_FAMILY,
      lineHeight: 1.25,
    },
    h3: {
      fontFamily: HEADER_FONT_FAMILY,
      lineHeight: 1.25,
    },
    h4: {
      fontFamily: HEADER_FONT_FAMILY,
      lineHeight: 1.25,
    },
    h5: {
      fontFamily: HEADER_FONT_FAMILY,
      lineHeight: 1.25,
    },
    h6: {
      fontFamily: HEADER_FONT_FAMILY,
      lineHeight: 1.25,
    },
    // General button css
    button: {
      fontFamily: HEADER_FONT_FAMILY,
      textTransform: null,
      fontSize: 16,
    },
    fontSize: 14,
  },
};

const canvasDimensionDesktop = 600;
const canvasDimensionMobile = 320;

const tempTheme = createTheme(_.merge({}, commonTheme));

// Add overrides late so that theme properties can be referenced
commonTheme.components = {
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: `${tempTheme.palette.primary.hover} !important`
        },
      },
    },
  },
  MuiButton: {
    defaultProps: {
      disableRipple: true,
    },
    styleOverrides: {
      contained: {
        '&:hover': {
          backgroundColor: tempTheme.palette.primary.hover,
          color: '#FFFFFF',
          boxShadow: '0px 10px 15px 0px #00000033',
        },
      },
      outlined: {
        borderColor: tempTheme.palette.primary.hover,
        '&:hover': {
          boxShadow: '0px 10px 15px 0px #00000033',
        },
      },
    },
  },
};

const muiTheme = createTheme(_.merge({}, commonTheme));

export default muiTheme;

export const ButtonStyled = styled(Button)({
  borderRadius: '32px',
  padding: '0.375rem 1.125rem',
  fontSize: '16px',
  width: '200px',

  '&.Mui-disabled': {
    'svg': {
      opacity: 0.4,
    },
  }
});

export const GenerateFormWrapper = styled(Box)({
  display: 'flex',
  margin: '24px',
  gap: '32px',
  flexDirection: 'column',
  alignItems: 'center',
});

export const InputSection = styled(Grid)(({ theme }) => ({
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'center',
  },
}));


export const FormLabelText = styled(FormLabel)({
  marginBottom: '8px',
  color: '#000000',
  fontSize: '16px',
  fontWeight: 600,
  lineHeight: '24px',
  textAlign: 'left',
});

export const ToggleButtonGroupStyled = styled(ToggleButtonGroup)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100% / 2, max(80px, 100% / 4)), 1fr))',
  gridGap: '24px',
  [`& .${toggleButtonGroupClasses.grouped}`]: {
    border: '1px solid #C4C4C4',
    borderRadius: '0px',
    backgroundColor: 'transparent',
    [`&.${toggleButtonGroupClasses.disabled}`]: {
      backgroundColor: 'rgba(0, 0, 0, .1)',
      border: '1px solid #C4C4C4',
      borderRadius: '0px',
    },
    [`&.${toggleButtonGroupClasses.selected}`]: {
      boxShadow: `0 0 4px 2px ${theme.palette.primary.hover}`,
      borderRadius: '10px',
      backgroundColor: 'transparent',
      border: `2px solid ${theme.palette.primary.hover}`,
      '&:hover': {
        backgroundColor: 'transparent',
      }
    },
    [`&:hover:not(${toggleButtonGroupClasses.selected})`]: {
      boxShadow: `0 0 0 2px ${theme.palette.primary.hover}`,
      backgroundColor: 'transparent',
    },
  },
}));

export const ToggleButtonStyled = styled(ToggleButton)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between'
});

export const ToggleButtonImage = styled('img')({
  width: '100%',
  objectFit: 'cover',
  aspectRatio: 1,
  borderRadius: '6px 6px 0px 0px',
});

export const ToggleButtonText = styled(Typography)({
  color: '#000000',
  fontSize: '11px',
});


export const ResultPageContainer = styled(Grid)(({ theme }) => ({
  flexDirection: 'column',
  alignItems: 'center',
  [theme.breakpoints.up('sm')]: {
    gap: '48px',
  },
  [theme.breakpoints.down('sm')]: {
    gap: '12px',
  },
}));
export const GeneratedImageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  border: '1px solid #C4C4C4',
  position: 'relative',
  [theme.breakpoints.up('sm')]: {
    width: `${canvasDimensionDesktop}px`,
    height: `${canvasDimensionDesktop}px`,
  },
  [theme.breakpoints.down('sm')]: {
    width: `${canvasDimensionMobile}px`,
    height: `${canvasDimensionMobile}px`,
  },
}));

export const GeneratedImageSectionContainer = styled(Grid)(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'grid',
    gridTemplateColumns: `auto ${canvasDimensionDesktop + 224}px auto`,
  },
  [theme.breakpoints.down('md')]: {
    gap: '24px',
    flexDirection: 'column-reverse',
    alignItems: 'center',
  },
}));

export const GeneratedImageColumn = styled(Box)(({ theme }) => ({
  [theme.breakpoints.up('sm')]: {
    width: `${canvasDimensionDesktop}px`,
    minHeight: `${canvasDimensionDesktop}px`,
    justifySelf: 'center',
    gridColumn: '2',
    gridRow: '1',
  },
  [theme.breakpoints.down('sm')]: {
    width: `${canvasDimensionMobile}px`,
    minHeight: `${canvasDimensionMobile}px`,
  },
}));

export const ActionButtonContainer = styled(Grid)(({ theme }) => ({
  width: 'fit-content',
  height: 'fit-content',

  [theme.breakpoints.up('md')]: {
    alignSelf: 'end',
    justifySelf: 'start',
    gridColumn: '2',
    gridRow: '1',
  },
  [theme.breakpoints.down('md')]: {
    alignSelf: 'center',
  },
}));

export const CTAButtonsContainer = styled(Grid)(({ theme }) => ({
  gap: '16px',
  [theme.breakpoints.up('sm')]: {
    justifyContent: 'center',
    flexDirection: 'row',
  },
  [theme.breakpoints.down('sm')]: {
    alignItems: 'center',
    flexDirection: 'column',
  },
}));

export const SubText = styled(Typography)(() => ({
  color: 'rgba(101,98,93,1)',
}));

export const IconButtonStyled = styled(IconButton)({
  width: '44px',
  height: '44px',
  '&:active': {
    border: '1.5px solid #1E93A5',
  },
});

export const ShareButtonsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  [theme.breakpoints.up('sm')]: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    zIndex: 1,
  },
  [theme.breakpoints.down('sm')]: {
    gap: '8px',
  },
}));
